/**
 * Container for the contract upload section.
 *
 * Owns the staged-files state, drives the upload via
 * {@link useContractUpload}, and surfaces validation rejections + a
 * combined error message to the view. The container also publishes the
 * `isUploading` flag (currently consumed locally; Wave 4+ will plumb it
 * back into the drawer container so the drawer can lock close while an
 * upload is in flight).
 * @module features/contracts/components/ContractUploadSection/ContractUploadSectionContainer
 */

import React, { memo, useCallback, useMemo, useRef, useState } from "react";

import { CONTRACT_DRAWER_TITLE_BY_DOCUMENT_TYPE } from "@/features/contracts/constants";
import { useContractDrawerState } from "@/features/contracts/hooks/useContractDrawerState";
import { useContractUpload } from "@/features/contracts/hooks/useContractUpload";
import { useIsContractOwner } from "@/features/contracts/hooks/useIsContractOwner";
import { useTriggerExtraction } from "@/features/contracts/hooks/useTriggerExtraction";
import type { StagedContractFile } from "@/features/contracts/types";

import ContractUploadSectionView from "./ContractUploadSectionView";

/** Props for {@link ContractUploadSectionContainer}. */
interface Props {
  /** Whether extraction data already exists for this player/document type. */
  readonly hasExtraction?: boolean;
}

/**
 * Container for the contract upload section.
 *
 * Reads the active player + document type from the contract drawer
 * store, manages staged files locally, and delegates the actual upload
 * to {@link useContractUpload}.
 * @param props - Component props
 * @param props.hasExtraction - Whether extraction data already exists
 * @returns The upload section view bound to live state, or null when
 * the drawer has no active player (defensive — the drawer container
 * only mounts this section in upload mode anyway).
 */
function ContractUploadSectionContainer({ hasExtraction = false }: Props) {
  const { state, setMode } = useContractDrawerState();
  const { playerId, playerName, documentType } = state;

  // SE-4350 defense-in-depth: refuse to render the upload form for
  // non-OWNER callers. The drawer already avoids entering `upload`
  // mode for them (because the Add Document CTA is hidden), but
  // this container-level short-circuit means a future embedder
  // (e.g. the Player Page Financials tab) inherits the gate for
  // free. Hooks that follow must still run unconditionally to
  // satisfy the rules-of-hooks; this is why the owner check sits
  // above the return instead of before the useContractUpload call.
  const isOwner = useIsContractOwner();

  const [stagedFiles, setStagedFiles] = useState<readonly StagedContractFile[]>(
    []
  );
  const [validationError, setValidationError] = useState<string | null>(null);

  // Guard against re-entry from fast double-taps on "Extract Key Terms":
  // the hook's `isUploading` flag is async (setState) and doesn't update
  // synchronously, so a second click before the state flush would still
  // see `isUploading=false` and fire a second batch. A ref flips
  // synchronously and blocks the second call.
  const extractInFlightRef = useRef(false);

  const { upload, isUploading, errorMessage, reset } = useContractUpload({
    playerId: playerId ?? "",
    documentType,
  });

  // SE-4710: pair the upload with an immediate extraction trigger so a
  // user who taps "Extract Key Terms" gets what the label promises in
  // one click. Pre-fix the button only uploaded, leaving the user to
  // hunt for an extract trigger in the document list — which on the
  // Transfer Agreement tab forced a switch to the Contract tab and
  // changed `state.documentType` to CONTRACT, causing the freshly
  // uploaded transfer agreement to be processed under the wrong type.
  const { triggerExtraction } = useTriggerExtraction();

  const title = useMemo(
    () => CONTRACT_DRAWER_TITLE_BY_DOCUMENT_TYPE[documentType],
    [documentType]
  );

  const handleFilesSelected = useCallback(
    (files: readonly StagedContractFile[]) => {
      // Clear any stale server error from a prior failed upload so the new
      // file selection does not inherit the previous failure's message.
      reset();
      setValidationError(null);
      setStagedFiles(prev => {
        const existingIds = new Set(prev.map(file => file.id));
        const additions = files.filter(file => !existingIds.has(file.id));
        return [...prev, ...additions];
      });
    },
    [reset]
  );

  const handleInvalidFiles = useCallback(
    (
      rejections: readonly { readonly name: string; readonly reason: string }[]
    ) => {
      if (rejections.length === 0) return;
      // Clear any stale server error so the fresh validation error is
      // what the user sees — otherwise a prior failed-upload message
      // would keep winning in `combinedError` (validationError has
      // lower precedence below).
      reset();
      const firstReason = rejections[0]?.reason ?? "Unsupported file.";
      const names = rejections.map(rejection => rejection.name).join(", ");
      setValidationError(`${firstReason} Rejected: ${names}`);
    },
    [reset]
  );

  const handleRemoveFile = useCallback((id: string) => {
    setStagedFiles(prev => prev.filter(file => file.id !== id));
  }, []);

  const handleExtract = useCallback(async () => {
    if (stagedFiles.length === 0) return;
    if (!playerId) return;
    if (extractInFlightRef.current) return;
    extractInFlightRef.current = true;
    try {
      await upload(stagedFiles);
      setStagedFiles([]);
      // SE-4336: on success, return to the list view so the user can
      // preview the freshly uploaded docs while extraction polls in
      // the background. The list container's `cache-and-network`
      // query refetches on mount, so the new document appears
      // automatically.
      setMode("list");
      try {
        // SE-4710: trigger extraction immediately against the active
        // documentType. Doing this here — while `state.documentType`
        // still matches the path the user came in on (CONTRACT for
        // "+ Add Document", TRANSFER_AGREEMENT for "+ Add Transfer
        // Agreement") — guarantees the resulting extraction is keyed
        // to the same type as the file the user just uploaded.
        // Failures surface via the trigger hook's own error state and
        // the document-list "Extract Key Terms" button remains as a
        // manual retry path.
        await triggerExtraction(playerId, documentType);
      } catch (error) {
        // Extraction failure does not affect upload cleanup.
        console.error("Failed to trigger extraction after upload", {
          error,
          playerId,
          documentType,
        });
      }
    } catch {
      // Error already surfaced via useContractUpload's errorMessage.
    } finally {
      extractInFlightRef.current = false;
    }
  }, [stagedFiles, playerId, upload, triggerExtraction, documentType, setMode]);

  const handleCancel = useCallback(() => {
    if (isUploading) return;
    reset();
    setStagedFiles([]);
    setValidationError(null);
    setMode("list");
  }, [isUploading, reset, setMode]);

  const combinedError = errorMessage ?? validationError;

  if (!playerId || !playerName) {
    return null;
  }

  if (!isOwner) return null;

  return (
    <ContractUploadSectionView
      title={title}
      documentType={documentType}
      canExtract={stagedFiles.length > 0 && !isUploading}
      bodyAssetLabel={
        documentType === "TRANSFER_AGREEMENT"
          ? "transfer agreement files"
          : "contract files"
      }
      playerName={playerName}
      stagedFiles={stagedFiles}
      isUploading={isUploading}
      errorMessage={combinedError}
      hasExtraction={hasExtraction}
      onFilesSelected={handleFilesSelected}
      onInvalidFiles={handleInvalidFiles}
      onRemoveFile={handleRemoveFile}
      onExtract={handleExtract}
      onCancel={handleCancel}
    />
  );
}

ContractUploadSectionContainer.displayName = "ContractUploadSectionContainer";

export default memo(ContractUploadSectionContainer);
